import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import {
    Home,
    Calendar,
    Phone,
    Users,
    LogIn,
    LogOut,
    Menu,
    X,
    Trophy,
    Building2,
    GraduationCap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

import tflogo from "./assets/tflogo.svg";

// Custom Social Media Icons (Standard SVGs)
const InstagramIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        stroke-width="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
    </svg>
);

const FacebookIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        stroke-width="0"
        viewBox="0 0 512 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path>
    </svg>
);

const LinkedinIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        stroke-width="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
    </svg>
);

const YoutubeIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        stroke-width="0"
        viewBox="0 0 576 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path>
    </svg>
);

const XIcon = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.005 4.066H5.028z" />
    </svg>
);

const WhatsAppIcon = ({ size = 20 }) => (
    <svg
        stroke="currentColor"
        fill="currentColor"
        stroke-width="0"
        viewBox="0 0 448 512"
        height={size}
        width={size}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
    </svg>
);

// ---------------------------------------------------------------------------
// Central nav config — edit here to add/remove links, no JSX changes needed
// ---------------------------------------------------------------------------
const topNavItems = [
    { label: "Accommodation", href: "/accommodation" },
    { label: "Workshops", href: "/workshops" },
    { label: "Competitions", href: "/competitions" },
    // { label: "Ignite", href: "/ignite" },
];

const eventsItems = [
    { label: "Lectures", href: "/lectures" },
    { label: "Exhibitions", href: "/exhibitions" },
    // { label: "Technoholix", href: "/technoholix" },
];

// Dynamic — add more entries any time, both the desktop popout and the
// mobile hamburger menu read from this same array.
const aboutItems = [
    { label: "History", href: "/history" },
    { label: "Media", href: "/media" },
    { label: "Gallery", href: "/gallery" },
    { label: "Recognition", href: "/recognition" },
    { label: "Legals", href: "/legals" },
];

const contactItem = { label: "Contact Us", href: "/contactus" };

const socialLinks = [
    {
        Icon: InstagramIcon,
        href: "https://www.instagram.com/techfest_iitbombay/?hl=en",
        label: "Instagram",
        color: "#E1306C",
    },
    {
        Icon: XIcon,
        href: "https://x.com/Techfest_IITB?lang=en",
        label: "X",
        color: "#1DA1F2",
    },
    {
        Icon: LinkedinIcon,
        href: "https://in.linkedin.com/company/techfest",
        label: "LinkedIn",
        color: "#0A66C2",
    },
    {
        Icon: FacebookIcon,
        href: "https://www.facebook.com/iitbombaytechfest/",
        label: "Facebook",
        color: "#1877F2",
    },
    {
        Icon: YoutubeIcon,
        href: "https://www.youtube.com/user/techfestiitbombay",
        label: "YouTube",
        color: "#FF0000",
    },
    {
        Icon: WhatsAppIcon,
        href: "https://whatsapp.com/channel/0029VaqxA9IAjPXM1339Pv41",
        label: "WhatsApp",
        color: "#25D366",
    },
];

const bottomNavItems = [
    { label: "Home", href: "/", Icon: Home },
    { label: "Events", Icon: Calendar, type: "popout", items: eventsItems },
    { label: "Competitions", href: "/competitions", Icon: Trophy },
    { label: "Accommodation", href: "/accommodation", Icon: Building2 },
    { label: "Workshops", href: "/workshops", Icon: GraduationCap },
];

const PopoutBox = ({
    isOpen,
    onClose,
    gsapLoaded,
    variant = "inline",
    inlineItems,
    fullscreenData,
    positionClass = "",
}) => {
    const [isMounted, setIsMounted] = useState(isOpen);
    const containerRef = useRef(null);
    const glassRef = useRef(null);
    const contentRef = useRef(null);
    const pixelsRef = useRef([]);

    const gridCols = variant === "fullscreen" ? 20 : 10;
    const pixelCount = gridCols * gridCols;

    useEffect(() => {
        if (isOpen) setIsMounted(true);
    }, [isOpen]);

    useEffect(() => {
        if (!isMounted || !gsap) return;

        const pixels = pixelsRef.current.filter(Boolean);

        // Clear previous animations to avoid conflicts
        gsap.killTweensOf(pixels);
        if (glassRef.current) gsap.killTweensOf(glassRef.current);
        if (contentRef.current) gsap.killTweensOf(contentRef.current.children);

        if (isOpen) {
            // Opening Animation Sequence
            const tl = gsap.timeline({
                onComplete: () => {
                    // Force clear stray pixels at the end to prevent sticking
                    gsap.set(pixels, { opacity: 0 });
                },
            });

            tl.set(glassRef.current, { visibility: "visible" }, 0)
                // 1. Glass Box expanding
                .fromTo(
                    glassRef.current,
                    { opacity: 0, scaleX: 0.1, transformOrigin: "left center" },
                    {
                        opacity: 1,
                        scaleX: 1,
                        duration: variant === "fullscreen" ? 0.55 : 0.45,
                        ease: "power2.inOut",
                    },
                    0, // Starts exactly at 0
                );

            // 2. Glitchy sweep, mapped synchronously to the expanding box
            pixels.forEach((pixel, i) => {
                const col = i % gridCols;
                const baseDelay =
                    (col / gridCols) * (variant === "fullscreen" ? 0.45 : 0.35);
                const randomDelay = Math.random() * 0.1; // Introduces heavy glitchy stagger
                const delay = baseDelay + randomDelay;
                const flashDuration = 0.02 + Math.random() * 0.05;

                // Randomize intensity and strictly turn off
                tl.set(pixel, { opacity: Math.random() * 0.6 + 0.4 }, delay);
                tl.set(pixel, { opacity: 0 }, delay + flashDuration);
            });

            // 3. Content fading in slightly delayed
            tl.fromTo(
                contentRef.current.children,
                { opacity: 0, x: -10 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.25,
                    stagger: 0.04,
                    ease: "power2.out",
                },
                0.15,
            );
        } else {
            // Closing Animation Sequence
            const revTl = gsap.timeline({
                onComplete: () => {
                    gsap.set(pixels, { opacity: 0 }); // Force clear
                    setIsMounted(false);
                },
            });

            // 1. Content fades out
            revTl
                .to(
                    contentRef.current.children,
                    {
                        opacity: 0,
                        x: -10,
                        duration: 0.2,
                        stagger: 0.03,
                    },
                    0,
                )
                // 2. Glass box scales down
                .to(
                    glassRef.current,
                    {
                        opacity: 0,
                        scaleX: 0.2,
                        duration: 0.4,
                        ease: "power2.inOut",
                    },
                    0.05,
                )
                .set(glassRef.current, { visibility: "hidden" });

            // 3. Glitch pixels sweep back right to left
            pixels.forEach((pixel, i) => {
                const col = i % gridCols;
                const revCol = gridCols - 1 - col;
                const baseDelay =
                    (revCol / gridCols) *
                    (variant === "fullscreen" ? 0.4 : 0.3);
                const randomDelay = Math.random() * 0.1;
                const delay = baseDelay + randomDelay;
                const flashDuration = 0.02 + Math.random() * 0.05;

                revTl.set(pixel, { opacity: Math.random() * 0.6 + 0.4 }, delay);
                revTl.set(pixel, { opacity: 0 }, delay + flashDuration);
            });
        }
    }, [isOpen, isMounted, gsapLoaded, variant, gridCols]);

    if (!isMounted) return null;

    return (
        <div
            className={`popout-container ${variant === "fullscreen" ? "fullscreen" : ""} ${positionClass}`}
            ref={containerRef}
        >
            {/* Pixel Animation Layer */}
            <div
                className={`pixels-layer ${variant === "fullscreen" ? "fullscreen-pixels" : ""}`}
                style={{
                    gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                    gridTemplateRows: `repeat(${gridCols}, 1fr)`,
                }}
            >
                {Array.from({ length: pixelCount }).map((_, i) => (
                    <div
                        key={i}
                        className="pixel"
                        ref={(el) => (pixelsRef.current[i] = el)}
                    />
                ))}
            </div>

            {/* Main Glass Content Box */}
            <div
                className={`popout-glass ${variant === "fullscreen" ? "fullscreen-glass" : ""}`}
                ref={glassRef}
                style={{ visibility: "hidden" }}
            >
                {variant === "fullscreen" ? (
                    <div className="fullscreen-content" ref={contentRef}>
                        <button
                            className="fullscreen-close"
                            onClick={onClose}
                            aria-label="Close menu"
                        >
                            <X size={26} />
                        </button>

                        <div className="fullscreen-col fullscreen-col-left">
                            {fullscreenData.about.map((item) => (
                                <a
                                    key={item.label}
                                    href={item.href}
                                    className="fullscreen-link"
                                    onClick={onClose}
                                >
                                    {item.label}
                                </a>
                            ))}
                            <a
                                href={fullscreenData.contact.href}
                                className="fullscreen-contact-btn"
                                onClick={onClose}
                            >
                                <Phone size={16} />
                                {fullscreenData.contact.label}
                            </a>
                        </div>

                        <div className="fullscreen-col fullscreen-col-right">
                            {fullscreenData.social.map(
                                ({ Icon, href, label, color }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="fullscreen-social-link"
                                        aria-label={label}
                                        style={{ "--brand": color }}
                                    >
                                        <Icon size={26} />
                                    </a>
                                ),
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="popout-content" ref={contentRef}>
                        {inlineItems.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                className="popout-item"
                                onClick={onClose}
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, loginWithGoogle, authFetch, logout } = useAuth();
    const [tfUser, setTfUser] = useState(null);
    const [activeMenu, setActiveMenu] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [gsapLoaded, setGsapLoaded] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    const isProfilePage = location.pathname === "/profile";

    useEffect(() => {
        if (currentUser && !tfUser) {
            authFetch("https://techfest.org/api/google-signin/", {
                method: "POST",
            })
                .then((res) => (res.ok ? res.json() : null))
                .then((data) => {
                    if (data) setTfUser(data);
                })
                .catch((err) =>
                    console.error("Failed to load TF profile", err),
                );
        }
    }, [currentUser, tfUser, authFetch]);

    const toggleMenu = (menu) => {
        setActiveMenu((prev) => (prev === menu ? null : menu));
    };

    // Global click listener to close popouts when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                !e.target.closest(".sidebar-item") &&
                !e.target.closest(".bottom-nav-item") &&
                !e.target.closest(".popout-container")
            ) {
                setActiveMenu(null);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    // Lock body scroll while the fullscreen mobile menu is open
    useEffect(() => {
        document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileMenuOpen]);

    // Logo Periodic Shine Animation
    useEffect(() => {
        if (!gsapLoaded || !gsap) return;
        const logoTl = gsap.timeline({ repeat: -1, repeatDelay: 3 });
        // Animates backgroundPosition across the text to simulate a shine
        logoTl.fromTo(
            ".logo-text",
            { backgroundPosition: "200% center" },
            {
                backgroundPosition: "-200% center",
                duration: 1.5,
                ease: "power2.inOut",
            },
        );
    }, [gsapLoaded]);

    return (
        <div className="app-wrapper">
            {/* CSS embedded within component to ensure single-file delivery */}
            <style
                dangerouslySetInnerHTML={{
                    __html: `
                * { box-sizing: border-box; margin: 0; padding: 0; }
                
                .app-wrapper {
                    position: fixed;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    pointer-events: none;
                    z-index: 100;
                }

                /* Logo Section */
                .logo-container {
                    position: absolute; top: 30px; left: 30px;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer; z-index: 20; pointer-events: auto;
                }
                .logo-img { height: 50px; vertical-align: middle; transition: height 0.2s ease; }
                .logo-text {
                    font-weight: 900; font-style: italic; 
                    font-size: 32px; letter-spacing: -2px; z-index: 2;
                    /* Gradient for the animated shine effect */
                    background: linear-gradient(120deg, #ffffff 40%, #88c0d0 50%, #ffffff 60%);
                    background-size: 300% 100%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 0 15px rgba(255,255,255,0.1);
                }

                /* Top Navigation */
                .top-nav {
                    position: absolute; top: 30px; left: 50%; transform: translateX(-50%);
                    display: flex; gap: 40px; align-items: center;
                    background: rgba(15, 25, 45, 0.6);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(150, 200, 255, 0.15);
                    border-top: 1px solid rgba(255, 255, 255, 0.3);
                    padding: 15px 50px;
                    border-radius: 8px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
                    z-index: 10; pointer-events: auto;
                }
                .top-nav-item {
                    color: #fff; text-decoration: none; font-size: 14px; font-weight: 500;
                    letter-spacing: 1.5px; cursor: pointer; transition: 0.3s;
                    text-transform: uppercase;
                }
                .top-nav-item:hover { color: #88c0d0; text-shadow: 0 0 10px rgba(136, 192, 208, 0.5); }
                .top-nav-item.active { font-weight: 700; }

                /* Top Right Sign In */
                .sign-in-btn {
                    position: absolute; top: 30px; right: 30px;
                    background: rgba(15, 25, 45, 0.6); backdrop-filter: blur(12px);
                    border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white; padding: 12px 24px; border-radius: 8px;
                    cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: 10px;
                    transition: 0.3s; letter-spacing: 1px; z-index: 10;
                    text-transform: uppercase; font-size: 13px; pointer-events: auto;
                }
                .sign-in-btn:hover {
                    background: rgba(40, 70, 120, 0.8);
                    box-shadow: 0 0 15px rgba(150, 200, 255, 0.3);
                }
                .sign-in-btn.sign-out {
                    border-color: rgba(239, 68, 68, 0.35);
                    background: rgba(30, 15, 25, 0.65);
                }
                .sign-in-btn.sign-out:hover {
                    background: rgba(180, 40, 40, 0.75);
                    box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
                    border-color: rgba(239, 68, 68, 0.6);
                }

                /* Logout Confirmation Modal */
                .logout-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(2, 6, 23, 0.75);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    pointer-events: auto;
                    padding: 20px;
                }
                .logout-modal-card {
                    background: rgba(15, 25, 45, 0.85);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.25);
                    border-top: 1px solid rgba(255, 255, 255, 0.4);
                    border-radius: 14px;
                    padding: 28px 24px;
                    max-width: 380px;
                    width: 100%;
                    text-align: center;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.05);
                    animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes modalPop {
                    from { transform: scale(0.92); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .logout-modal-icon {
                    width: 54px;
                    height: 54px;
                    margin: 0 auto 16px;
                    border-radius: 50%;
                    background: rgba(239, 68, 68, 0.15);
                    border: 1px solid rgba(239, 68, 68, 0.4);
                    color: #ef4444;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .logout-modal-title {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #ffffff;
                    margin-bottom: 8px;
                    letter-spacing: -0.3px;
                }
                .logout-modal-message {
                    font-size: 0.95rem;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 24px;
                    line-height: 1.5;
                }
                .logout-modal-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: center;
                }
                .logout-modal-btn {
                    flex: 1;
                    padding: 11px 18px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    text-transform: uppercase;
                }
                .logout-modal-btn.cancel {
                    background: rgba(255, 255, 255, 0.08);
                    color: #e2e8f0;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                }
                .logout-modal-btn.cancel:hover {
                    background: rgba(255, 255, 255, 0.15);
                    color: #ffffff;
                }
                .logout-modal-btn.confirm {
                    background: rgba(239, 68, 68, 0.85);
                    color: #ffffff;
                    box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
                }
                .logout-modal-btn.confirm:hover {
                    background: #dc2626;
                    box-shadow: 0 0 20px rgba(239, 68, 68, 0.6);
                    transform: translateY(-1px);
                }

                /* Sidebars */
                .sidebar {
                    position: absolute; top: 50%; transform: translateY(-50%);
                    display: flex; flex-direction: column; gap: 0px;
                    background: rgba(10, 15, 30, 0.6);
                    backdrop-filter: blur(15px);
                    border: 1px solid rgba(150, 200, 255, 0.15);
                    padding: 15px 5px;
                    border-radius: 12px;
                    z-index: 10; pointer-events: auto;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.6);
                }
                .sidebar.left { left: 20px; }
                .sidebar.right { right: 30px; padding: 18px 12px; gap: 6px; }

                .sidebar.left .sidebar-item {
                    color: #ffffff;
                }
                
                .sidebar-item {
                    display: flex; flex-direction: column; align-items: center;
                    color: #a0aec0; cursor: pointer; transition: 0.3s;
                    font-size: 10px; gap: 6px; text-transform: capitalize; 
                    letter-spacing: 0.5px; padding: 12px 5px; border-radius: 8px;
                    position: relative;
                    text-decoration: none;
                    background: none; border: none;
                }
                .sidebar-item:hover, .sidebar-item.active { 
                    color: #6fc4f2;
                }

                .sidebar.left .sidebar-item:hover {
                    color: #6fc4f2;
                    text-shadow: 0 0 10px rgba(255,255,255,0.4);
                }
                .sidebar-item svg { stroke-width: 1.5; }

                /* Right sidebar (social media) icons: larger and white */
                /* Right sidebar (social media) icons: larger and white */
                .sidebar.right .sidebar-item {
                    color: #ffffff;
                    padding: 10px;
                    transition: color 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                .sidebar.right .sidebar-item:hover {
                    color: var(--brand, #88c0d0);
                    transform: rotate(12deg) scale(1.15);
                    text-shadow: 0 0 10px rgba(255,255,255,0.4);
                }

                /* Popout Animations and Styling (inline variant) */
                .popout-container {
                    position: absolute; 
                    left: 75px; 
                    top: 50%;
                    transform: translateY(-50%);
                    width: max-content; 
                    min-width: 140px;
                    z-index: 5;
                    pointer-events: none;
                }
                .pixels-layer {
                    position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                    display: grid; 
                    z-index: 2;
                }
                .pixel { background: rgba(255,255,255,0.9); opacity: 0; box-shadow: 0 0 5px white; }
                
                .popout-glass {
                    position: relative; 
                    width: 100%; height: 100%;
                    background: rgba(15, 25, 45, 0.85);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(150, 200, 255, 0.3);
                    border-radius: 12px;
                    padding: 12px 16px;
                    box-shadow: 0 15px 40px rgba(0,0,0,0.7), inset 0 0 20px rgba(255,255,255,0.05);
                    pointer-events: auto;
                }
                .popout-content {
                    display: flex; flex-direction: column; gap: 6px;
                }
                .popout-item {
                    color: #e2e8f0; font-size: 11px; text-transform: uppercase; 
                    letter-spacing: 1.5px; cursor: pointer; transition: 0.2s; 
                    padding: 6px 4px; border-bottom: 1px solid rgba(255,255,255,0.1);
                    font-weight: 500;
                    text-decoration: none; display: block;
                }
                .popout-item:hover { 
                    color: #63b3ed; 
                    padding-left: 10px; 
                    border-color: #63b3ed; 
                    background: linear-gradient(90deg, rgba(99,179,237,0.1) 0%, transparent 100%);
                }

                /* Fullscreen hamburger menu variant */
                .hamburger-btn {
                    display: none;
                    position: fixed; top: 18px; left: 18px;
                    background: rgba(15, 25, 45, 0.65); backdrop-filter: blur(12px);
                    border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white; border-radius: 8px;
                    width: 42px; height: 42px;
                    align-items: center; justify-content: center;
                    cursor: pointer; z-index: 250; pointer-events: auto;
                }

                .popout-container.fullscreen {
                    position: fixed; inset: 0; left: 0; top: 0;
                    width: 100vw; height: 100vh;
                    transform: none;
                    min-width: 0;
                    z-index: 200;
                }
                .pixels-layer.fullscreen-pixels {
                    position: fixed; inset: 0;
                }
                .popout-glass.fullscreen-glass {
                    width: 100vw; height: 100vh;
                    background: rgba(6, 10, 20, 0.97);
                    border: none; border-radius: 0;
                    padding: 90px 28px 40px;
                    box-shadow: none;
                }
                .fullscreen-content {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    height: 100%;
                    gap: 48px;
                }
                .fullscreen-close {
                    position: absolute; top: 18px; right: 18px;
                    background: rgba(255,255,255,0.06);
                    border: 1px solid rgba(150, 200, 255, 0.2);
                    color: white; border-radius: 8px;
                    width: 42px; height: 42px;
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                }
                .fullscreen-col {
                    display: flex; flex-direction: column;
                }
                .fullscreen-col-left {
                    gap: 22px;
                }
                .fullscreen-link {
                    color: #ffffff; text-decoration: none;
                    font-size: 26px; font-weight: 700; text-transform: uppercase;
                    letter-spacing: 1.5px;
                }
                .fullscreen-link:active { color: #6fc4f2; }
                .fullscreen-contact-btn {
                    margin-top: 12px;
                    display: inline-flex; align-items: center; gap: 10px;
                    width: fit-content;
                    color: #ffffff; text-decoration: none;
                    border: 1px solid rgba(150, 200, 255, 0.4);
                    border-radius: 8px;
                    padding: 12px 22px;
                    font-size: 13px; font-weight: 600; letter-spacing: 1px;
                    text-transform: uppercase;
                    background: rgba(111, 196, 242, 0.08);
                }
                .fullscreen-col-right {
                    flex-direction: row; flex-wrap: wrap;
                    gap: 22px; align-items: center;
                }
                .fullscreen-social-link {
                    color: #ffffff;
                    display: flex; align-items: center; justify-content: center;
                    transition: color 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
                }
                .fullscreen-social-link:hover,
                .fullscreen-social-link:active {
                    color: var(--brand, #88c0d0);
                    transform: rotate(12deg) scale(1.15);
                }

                /* Bottom mobile nav */
                .bottom-nav {
                    display: none;
                    position: fixed; bottom: 0; left: 0; width: 100%;
                    background: rgba(10, 15, 30, 0.85);
                    backdrop-filter: blur(15px);
                    border-top: 1px solid rgba(150, 200, 255, 0.15);
                    padding: 8px 4px calc(8px + env(safe-area-inset-bottom));
                    z-index: 150; pointer-events: auto;
                    justify-content: space-around; align-items: center;
                }
                .bottom-nav-item {
                    display: flex; 
                    flex-direction: column; 
                    align-items: center;
                    gap: 4px; 
                    color: #a0aec0; 
                    text-decoration: none;
                    font-size: 7px; 
                    letter-spacing: 0.3px; 
                    text-transform: uppercase;
                    font-weight: 500; 
                    flex: 1; 
                    text-align: center;
                    cursor: pointer;
                    position: relative;
                }
                .bottom-nav-item:active { color: #6fc4f2; }
                .bottom-nav-item svg { stroke-width: 1.5; }

                .popout-container.bottom-popout {
                    left: 50%;
                    top: auto;
                    bottom: 100%;
                    transform: translateX(-50%);
                    margin-bottom: 14px;
                    z-index: 160;
                }

                /* ---------------- Responsive: 1024px ---------------- */
                @media (max-width: 1024px) {
                    .logo-img { height: 42px; }
                    .top-nav { gap: 26px; padding: 12px 32px; }
                    .top-nav-item { font-size: 12.5px; letter-spacing: 1.2px; }
                    .sign-in-btn { padding: 10px 18px; font-size: 12px; }
                    .sidebar.left, .sidebar.right { padding: 10px 4px; }
                    .sidebar.right { padding: 14px 9px; gap: 4px; }
                    .sidebar-item { padding: 10px 4px; font-size: 9px; gap: 5px; }
                    .sidebar-item svg { width: 19px; height: 19px; }
                    .sidebar.right .sidebar-item svg { width: 22px; height: 22px; }
                    .popout-container { left: 66px; }
                }

                /* ---------------- Responsive: 768px ---------------- */
                @media (max-width: 768px) {
                    .logo-img { height: 34px; }
                    .logo-container { top: 20px; left: 20px; }
                    .top-nav { top: 20px; gap: 16px; padding: 10px 20px; border-radius: 6px; }
                    .top-nav-item { font-size: 10.5px; letter-spacing: 1px; }
                    .sign-in-btn { top: 20px; right: 20px; padding: 8px 14px; font-size: 11px; gap: 6px; }
                    .sidebar { padding: 8px 3px; border-radius: 10px; }
                    .sidebar.right { padding: 10px 7px; gap: 3px; }
                    .sidebar.left { left: 12px; }
                    .sidebar.right { right: 14px; }
                    .sidebar-item { padding: 8px 3px; font-size: 8px; gap: 4px; }
                    .sidebar-item svg { width: 16px; height: 16px; }
                    .sidebar.right .sidebar-item svg { width: 19px; height: 19px; }
                    .popout-container { left: 52px; }
                    .popout-item { font-size: 10px; }
                }

                /* ---------------- Responsive: 480px (mobile) ---------------- */
                @media (max-width: 480px) {
                    .top-nav { display: none; }
                    .sidebar.left, .sidebar.right { display: none; }

                    .hamburger-btn { display: flex; }
                    .bottom-nav { display: flex; }

                    .logo-container { top: 16px; left: 68px; }
                    .logo-img { height: 28px; }

                    .sign-in-btn {
                        top: 16px; right: 16px; padding: 8px 12px; font-size: 10px; gap: 5px;
                        border-radius: 6px;
                    }

                    .fullscreen-glass { padding: 84px 22px 34px; }
                    .fullscreen-link { font-size: 22px; }
                    .fullscreen-contact-btn { font-size: 12px; padding: 10px 18px; }
                    .fullscreen-social-link svg { width: 24px; height: 24px; }
                }
            `,
                }}
            />

            {/* Hamburger (mobile only) */}
            <button
                className="hamburger-btn"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Fullscreen hamburger menu (mobile only) */}
            <PopoutBox
                variant="fullscreen"
                isOpen={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                gsapLoaded={gsapLoaded}
                fullscreenData={{
                    about: aboutItems,
                    contact: contactItem,
                    social: socialLinks,
                }}
            />

            {/* Logo Area */}
            <a href="/" className="logo-container">
                <div className="logo-text">
                    <img src={tflogo} alt="TF Logo" className="logo-img" />
                </div>
            </a>

            {/* Top Navigation */}
            <div className="top-nav">
                {topNavItems.map((item) => (
                    <a
                        key={item.label}
                        href={item.href}
                        className={`top-nav-item ${item.active ? "active" : ""}`}
                    >
                        {item.label}
                    </a>
                ))}
            </div>

            {/* Sign In / Profile / Sign Out Button */}
            {isProfilePage ? (
                <div
                    className="sign-in-btn sign-out"
                    onClick={() => setShowLogoutModal(true)}
                >
                    <LogOut size={16} /> Sign Out
                </div>
            ) : (
                <div
                    className="sign-in-btn"
                    onClick={async () => {
                        if (tfUser || currentUser) {
                            navigate("/profile");
                        } else {
                            try {
                                if (!currentUser) await loginWithGoogle();
                                const res = await authFetch(
                                    "https://techfest.org/api/google-signin/",
                                    { method: "POST" },
                                );
                                if (res.ok) {
                                    const data = await res.json();
                                    setTfUser(data);
                                }
                            } catch (e) {
                                console.error("Login failed", e);
                            }
                        }
                    }}
                >
                    {tfUser ? (
                        <>
                            <img
                                src={`https://techfest.org/media/${tfUser.avatar}.png`}
                                alt="avatar"
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                }}
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                            {tfUser.name
                                ? tfUser.name.split(" ")[0]
                                : "Profile"}
                        </>
                    ) : currentUser ? (
                        <>
                            {currentUser.displayName
                                ? currentUser.displayName.split(" ")[0]
                                : "Profile"}
                        </>
                    ) : (
                        <>
                            <LogIn size={16} /> Sign In
                        </>
                    )}
                </div>
            )}

            {/* Sign Out Confirmation Modal */}
            {showLogoutModal && (
                <div
                    className="logout-modal-backdrop"
                    onClick={() => setShowLogoutModal(false)}
                >
                    <div
                        className="logout-modal-card"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="logout-modal-icon">
                            <LogOut size={26} />
                        </div>
                        <h3 className="logout-modal-title">Sign Out</h3>
                        <p className="logout-modal-message">
                            Are you sure you want to sign out?
                        </p>
                        <div className="logout-modal-actions">
                            <button
                                className="logout-modal-btn cancel"
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="logout-modal-btn confirm"
                                onClick={async () => {
                                    setShowLogoutModal(false);
                                    try {
                                        await logout();
                                        setTfUser(null);
                                        navigate("/");
                                    } catch (err) {
                                        console.error("Logout failed", err);
                                    }
                                }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Left Glassy Sidebar (desktop / tablet) */}
            <div className="sidebar left">
                <a href="/" className="sidebar-item">
                    <Home size={26} />
                    <span>Home</span>
                </a>

                <div
                    className={`sidebar-item ${activeMenu === "events" ? "active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu("events");
                    }}
                >
                    <Calendar size={26} />
                    <span>Events</span>
                    <PopoutBox
                        isOpen={activeMenu === "events"}
                        inlineItems={eventsItems}
                        onClose={() => setActiveMenu(null)}
                        gsapLoaded={gsapLoaded}
                    />
                </div>

                <a href="/contactus" className="sidebar-item">
                    <Phone size={26} />
                    <span>Contact Us</span>
                </a>

                <div
                    className={`sidebar-item ${activeMenu === "about" ? "active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu("about");
                    }}
                >
                    <Users size={26} />
                    <span>About Us</span>
                    <PopoutBox
                        isOpen={activeMenu === "about"}
                        inlineItems={aboutItems}
                        onClose={() => setActiveMenu(null)}
                        gsapLoaded={gsapLoaded}
                    />
                </div>
            </div>

            {/* Right Glassy Sidebar (Social Media) - desktop / tablet */}
            <div className="sidebar right">
                {socialLinks.map(({ Icon, href, label, color }) => (
                    <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-item"
                        aria-label={label}
                        style={{ "--brand": color }}
                    >
                        <Icon size={26} />
                    </a>
                ))}
            </div>

            {/* Bottom nav (mobile only) */}
            <div className="bottom-nav">
                {bottomNavItems.map((item) => {
                    const { label, Icon } = item;

                    if (item.type === "popout") {
                        return (
                            <div
                                key={label}
                                className={`bottom-nav-item ${activeMenu === "mobileEvents" ? "active" : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMenu("mobileEvents");
                                }}
                            >
                                <Icon size={20} />
                                <span>{label}</span>
                                <PopoutBox
                                    isOpen={activeMenu === "mobileEvents"}
                                    inlineItems={item.items}
                                    onClose={() => setActiveMenu(null)}
                                    gsapLoaded={gsapLoaded}
                                    positionClass="bottom-popout"
                                />
                            </div>
                        );
                    }

                    return (
                        <a
                            key={label}
                            href={item.href}
                            className="bottom-nav-item"
                        >
                            <Icon size={20} />
                            <span>{label}</span>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
